import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@heroui/react';
import { DataGrid } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import type { ShiftLedgerAccountRow, ShiftLedgerSummary } from '@/api/models';
import { getNodeTestIdProp } from '@/utils';
import { formatTetri } from '../../shiftsMeta';

type ShiftLedgerAccountsTableProps = {
    summary: ShiftLedgerSummary;
};

/**
 * What the shift posted per account, in the server's code order, with the
 * grand totals under it (render-only). Amounts are tetri.
 */
export const ShiftLedgerAccountsTable: React.FC<
    ShiftLedgerAccountsTableProps
> = ({ summary }) => {
    const { t } = useTranslation('pages');

    const columns = React.useMemo<DataGridColumn<ShiftLedgerAccountRow>[]>(
        () => [
            {
                id: 'account',
                header: t('shifts.summary.columns.account'),
                isRowHeader: true,
                cell: (row) => (
                    <span className="text-sm">
                        <span className="font-mono text-xs text-muted">
                            {row.code}
                        </span>{' '}
                        {row.name}
                    </span>
                ),
            },
            {
                id: 'debit',
                header: t('shifts.summary.columns.debit'),
                width: 130,
                align: 'end',
                cell: (row) => (
                    <span className="text-sm tabular-nums">
                        {row.debitTetri ? formatTetri(row.debitTetri) : '—'}
                    </span>
                ),
            },
            {
                id: 'credit',
                header: t('shifts.summary.columns.credit'),
                width: 130,
                align: 'end',
                cell: (row) => (
                    <span className="text-sm tabular-nums">
                        {row.creditTetri ? formatTetri(row.creditTetri) : '—'}
                    </span>
                ),
            },
        ],
        [t],
    );

    return (
        <div className="flex flex-col gap-2">
            <div {...getNodeTestIdProp('shift-ledger-summary-accounts')}>
                <DataGrid<ShiftLedgerAccountRow>
                    aria-label={t('shifts.summary.accountsAria')}
                    columns={columns}
                    data={summary.accounts}
                    getRowId={(row) => row.accountId}
                    variant="secondary"
                />
            </div>
            <div className="flex items-center justify-between px-3">
                <Typography type="body-sm" weight="semibold">
                    {t('shifts.summary.total')}
                </Typography>
                <div className="flex gap-6">
                    <Typography
                        className="tabular-nums"
                        type="body-sm"
                        weight="semibold"
                        {...getNodeTestIdProp('shift-ledger-summary-debit')}
                    >
                        {formatTetri(summary.totalDebitTetri)}
                    </Typography>
                    <Typography
                        className="tabular-nums"
                        type="body-sm"
                        weight="semibold"
                        {...getNodeTestIdProp('shift-ledger-summary-credit')}
                    >
                        {formatTetri(summary.totalCreditTetri)}
                    </Typography>
                </div>
            </div>
        </div>
    );
};
